/**
 * Network Monitor Utility
 * Detects online/offline status and triggers callbacks
 * Following SOLID principles - Single Responsibility: Network status monitoring only
 */

/**
 * Network Monitor Class
 * Monitors network connectivity and provides event callbacks
 */
class NetworkMonitor {
    constructor() {
        this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
        this.listeners = new Set();
        this.boundOnline = this.handleOnline.bind(this);
        this.boundOffline = this.handleOffline.bind(this);
        this.initialize();
    }

    /**
     * Initialize event listeners
     */
    initialize() {
        // Only initialize if window is available (e.g., not in test environment)
        if (typeof window !== 'undefined' && window.addEventListener) {
            // Check if listeners are already attached to avoid duplicates
            // Remove existing listeners first if they exist (in case of re-initialization)
            if (this._listenersAttached) {
                this._removeListeners();
            }
            
            // Listen to online/offline events
            window.addEventListener('online', this.boundOnline);
            window.addEventListener('offline', this.boundOffline);
            this._listenersAttached = true;
        }
    }

    /**
     * Remove event listeners (internal method)
     * @private
     */
    _removeListeners() {
        if (typeof window !== 'undefined' && window.removeEventListener) {
            window.removeEventListener('online', this.boundOnline);
            window.removeEventListener('offline', this.boundOffline);
        }
        this._listenersAttached = false;
    }

    /**
     * Handle online event
     */
    handleOnline() {
        this.isOnline = true;
        this.notifyListeners(true);
    }

    /**
     * Handle offline event
     */
    handleOffline() {
        this.isOnline = false;
        this.notifyListeners(false);
    }

    /**
     * Notify all registered listeners
     * @param {boolean} online - Online status
     */
    notifyListeners(online) {
        this.listeners.forEach(listener => {
            try {
                listener(online);
            } catch (error) {
                console.error('Error in network status listener:', error);
            }
        });
    }

    /**
     * Get current online status
     * @returns {boolean} True if online
     */
    getStatus() {
        return typeof navigator !== 'undefined' ? navigator.onLine : this.isOnline;
    }

    /**
     * Register a callback for network status changes
     * @param {Function} callback - Callback function (online: boolean) => void
     * @returns {Function} Unsubscribe function
     */
    onStatusChange(callback) {
        if (typeof callback !== 'function') {
            throw new Error('Callback must be a function');
        }

        this.listeners.add(callback);

        // Return unsubscribe function
        return () => {
            this.listeners.delete(callback);
        };
    }

    /**
     * Remove all listeners and cleanup
     * Note: For singleton instances, consider re-initializing instead of destroying
     */
    destroy() {
        this._removeListeners();
        this.listeners.clear();
    }
}

// Export singleton instance
export const networkMonitor = new NetworkMonitor();
export default networkMonitor;